import { Alert, Box, Grid } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CargoType, CarrierType, CarrierVisitDirection, OrderResponseDto } from '@planning/app/api'
import { InitializationWrapper } from '@planning/components'
import { useGeneralCargoLocations } from '@planning/hooks/useGeneralCargoLocations'
import { ContainerYardOperationViewStore, orderListParsingService } from '@planning/index'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { ValidateOutboundOrderFunc } from '@planning/stores/gateControl/ValidateOutboundDto'
import { GetOrderDamagesAmountFunc } from '@planning/stores/generalCargo/OrderDamageDto'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { downloadFile, PATHS, useAsyncFetch } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import Papa from 'papaparse'
import { createContext, useContext, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { GeneralCargoOrderLocationItem } from '../GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { RenderIsoCodeMappingInputFn } from '../Order/stores/IsoCodeMappingInputStore'
import { BaplieParseResult } from '../Order/stores/OrderListUploadParsingStore'
import { CarrierVisitDashboard } from '../VesselVisit/Components/CarrierVisitDashboard'
import { RailVisitDashboardHeader } from './Components/Dashboard/RailVisitDashboardHeader'
import { RailVisitOverview } from './Components/Dashboard/RailVisitOverview'
import { RailVisitDischargePage } from './RailVisitDischargePage'
import { RailVisitLoadPage } from './RailVisitLoadPage'

// [OCTA-948] TODO: Adjust for GC
export const orderSortingDelegate = (_: any, a: IOrderItem, b: IOrderItem) => {
  return (a.data.containerNumber ?? '').localeCompare(b.data.containerNumber ?? '')
}

export const orderFilterDelegate = (
  filter: string,
  item: IOrderItem,
  _?: boolean,
  __?: Date,
  ___?: Date,
  filterStatus?: string,
) => {
  const data = item.data
  if (filterStatus && data.status !== filterStatus) {
    return false
  }

  if (data.containerNumber) return data.containerNumber.toLowerCase().includes(filter.toLowerCase())
  if (data.commodityName)
    return (
      (data.commodityName.toLowerCase().includes(filter.toLowerCase()) ||
        data.referenceNumber?.toLowerCase().includes(filter.toLowerCase()) ||
        data.customerName?.toLowerCase().includes(filter.toLowerCase())) ??
      false
    )

  return false
}

const Context = createContext<ContainerYardOperationViewStore | null>(null)

export const ContainerYardOperationProvider = Context.Provider

export const useContainerYardOperationStore = () => {
  const context = useContext(Context)
  return context
}

interface Props {
  renderDamage?: (orderId: number) => React.ReactElement
  renderIsoCodeMappingInput?: RenderIsoCodeMappingInputFn
  getOrderDamagesAmountFunc?: GetOrderDamagesAmountFunc
  validateOutboundRequest?: ValidateOutboundOrderFunc
  equipmentPlanningSummary?: React.ReactElement
  unresolvedOperationsIssueCount?: number
  containerYardOperationViewStore: ContainerYardOperationViewStore
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
  onOpenGeneralCargoReservation?: (order: OrderResponseDto) => void
  getOrderLocationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; locations: GeneralCargoOrderLocationItem[] }>>
  getOrderReservationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; planned: GeneralCargoOrderLocationItem[] }>>
  reservationUpdate?: { orderId: number; reservations: GeneralCargoOrderLocationItem[] }
}
export const RailVisitDetailsPage = observer(
  ({
    renderDamage,
    renderIsoCodeMappingInput,
    getOrderDamagesAmountFunc,
    validateOutboundRequest,
    equipmentPlanningSummary,
    unresolvedOperationsIssueCount,
    containerYardOperationViewStore,
    parseBaplie,
    onOpenGeneralCargoReservation,
    getOrderLocationsFunc,
    getOrderReservationsFunc,
    reservationUpdate,
  }: Props) => {
    const {
      railVisitItemStore,
      railVisitQueryService,

      generalCargoViewStore,
      issueItemStore,
      isoCodeMappingInputStore,
      railcarTrackPositionItemStore,
      orderItemStore,
      containerItemStore,
    } = usePlanningStore()
    const { id, page } = useParams()
    const navigate = useNavigate()
    const theme = useMinimalsTheme()

    const detailPages = {
      dashboard: 'dashboard',
      discharge: 'discharge',
      load: 'load',
      restow: 'restow',
    }

    const currentPage =
      Object.keys(detailPages).find(key => key === page?.toLowerCase()) ?? detailPages.dashboard

    const railVisitId = Number(id)
    if (!railVisitId) {
      navigate(PATHS.visits.railVisits.url)
    }

    const handlePageChange = (page: string) => {
      if (page === `${PATHS.issues.url}`) {
        navigate(`${PATHS.issues.url}`, {
          state: { visitId: railVisitId },
        })
      } else navigate(`${PATHS.visits.railVisits.url}/${railVisitId}/${page}`)
    }

    isoCodeMappingInputStore.renderIsoCodeMappingInput = renderIsoCodeMappingInput

    //TODO: the 'railVisitItem' is also a computed prop in RailcarSequencingStore. After integrating the feature flag, get rid of the computed value
    const railVisitItem = computed(() => railVisitItemStore.elements[railVisitId]).get()

    useAsyncFetch(async () => {
      await railVisitQueryService.fetchById(railVisitId)
      await orderItemStore.fetchByVisitId(railVisitId)
      await containerItemStore.fetchByVisitId(railVisitId)
      await issueItemStore.fetch()
    }, [railVisitId, railVisitQueryService, issueItemStore, containerItemStore, orderItemStore])

    const orders = computed(() =>
      railVisitItemStore.ordersByRailVisitId
        ? railVisitItemStore.ordersByRailVisitId[railVisitId]
        : [],
    ).get()

    //Used to fetch orders damage when the visit is GC and the order list is available
    useEffect(() => {
      const fetchDamages = async () => {
        if (
          railVisitItem &&
          railVisitItem.data.cargoType === CargoType.GeneralCargo &&
          orders?.length &&
          getOrderDamagesAmountFunc
        ) {
          generalCargoViewStore.setDamageOrders(
            await getOrderDamagesAmountFunc(orders.map(x => x.id)),
          )
        }
      }

      fetchDamages()
    }, [generalCargoViewStore, getOrderDamagesAmountFunc, orders, railVisitItem])

    const handleExportOrders = (currentPage: string) => {
      const direction =
        currentPage === detailPages.discharge
          ? CarrierVisitDirection.Inbound
          : CarrierVisitDirection.Outbound

      const { fileName, header, data } = orderListParsingService.parseOrderListExport(
        CarrierType.Train,
        direction,
        railVisitItem,
        railcarTrackPositionItemStore.elements,
      )

      const csvData = Papa.unparse({
        fields: header,
        data: data,
      })

      downloadFile(new Blob([csvData as BlobPart]), fileName)
    }

    const dischargeOrdersData = useMemo(() => {
      return railVisitItem?.discharge?.generalCargoOrders?.map(order => order.data) ?? []
    }, [railVisitItem?.discharge?.generalCargoOrders])

    const { orderLocationsById, orderReservationsById } = useGeneralCargoLocations(
      dischargeOrdersData,
      getOrderLocationsFunc,
      getOrderReservationsFunc,
      reservationUpdate,
    )

    return (
      <ContainerYardOperationProvider value={containerYardOperationViewStore}>
        <InitializationWrapper isInitializing={!railVisitItem}>
          <Box sx={{ backgroundColor: theme.palette.grey[200], minHeight: '100vh' }}>
            {currentPage === detailPages.dashboard && (
              <>
                <RailVisitDashboardHeader railVisitItem={railVisitItem} />
                <Grid
                  sx={{ padding: theme.spacing(3) }}
                  container
                  justifyContent='flex-start'
                  spacing={{ xs: 1, md: 3 }}
                >
                  <Grid size={12}>
                    <RailVisitOverview railVisitItem={railVisitItem} />
                  </Grid>
                </Grid>
                <CarrierVisitDashboard
                  visitType={CarrierType.Train}
                  detailPages={detailPages}
                  railVisitItem={railVisitItem}
                  handlePageChange={handlePageChange}
                  onClickExportOrders={direction => {
                    const operation =
                      direction === CarrierVisitDirection.Inbound ? 'discharge' : 'load'
                    handleExportOrders(operation)
                  }}
                  unresolvedOperationsIssueCount={unresolvedOperationsIssueCount}
                  parseBaplie={parseBaplie}
                  equipmentPlanningSummary={equipmentPlanningSummary}
                />
              </>
            )}

            {currentPage === detailPages.discharge && railVisitItem && railVisitItem.orders && (
              <RailVisitDischargePage
                onOpenGeneralCargoReservation={onOpenGeneralCargoReservation}
                orderLocationsById={orderLocationsById}
                orderReservationsById={orderReservationsById}
              />
            )}
            {currentPage === detailPages.load && railVisitItem && railVisitItem.orders && (
              <RailVisitLoadPage
                onOpenGeneralCargoReservation={onOpenGeneralCargoReservation}
                orderReservationsById={orderReservationsById}
              />
            )}
          </Box>
        </InitializationWrapper>
      </ContainerYardOperationProvider>
    )
  },
)

export const OperationStartedAlert = () => {
  const { t } = useTranslate()
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Alert severity='warning'>
        {t(
          'operationStartedAnyChangesWillHaveImpactRecordedByRailTally',
          'Operation has already started. Any changes done on this page will have an impact on data recorded by the Rail Tally',
        )}
      </Alert>
    </Box>
  )
}
